/**
 * 预览页内的"输入组件"小工具
 * ------------------------------------------------------------
 * 预览页要做的事情很重复：把草稿数组渲染成可编辑表格、把编辑结果写回草稿。
 * 这里用「config 驱动」的通用表格，避免为每种内容写一遍。
 */
import { escapeHtml } from '../utils/index.js';

/**
 * 可编辑表格
 * @param {object} cfg
 * @param {string} cfg.key        草稿里的字段名（words/sentences/grammar/questions）
 * @param {Array<{field:string,label:string,type?:'text'|'textarea'|'select'|'tags',options?:string[],width?:string}>} cfg.columns
 * @param {any[]} cfg.rows
 * @param {(row,field,value)=>void} cfg.onEdit
 * @param {(row)=>void} cfg.onDelete
 */
export function editableTable({ key, columns, rows, onEdit, onDelete }) {
  if (!rows.length) {
    return `<p class="muted" style="padding:16px;text-align:center">没有识别到内容</p>`;
  }

  const head = `<tr>
    <th style="width:34px">#</th>
    ${columns.map(c => `<th style="${c.width ? `width:${c.width}` : ''}">${escapeHtml(c.label)}</th>`).join('')}
    <th style="width:110px">状态</th>
    <th style="width:50px"></th>
  </tr>`;

  const body = rows.map((row, i) => {
    const cells = columns.map(c => {
      const val = row[c.field] ?? '';
      const cls = `cell-edit ${row.needsReview ? 'need' : ''}`;
      if (c.type === 'textarea') {
        return `<td><textarea class="${cls}" data-key="${key}" data-i="${i}" data-field="${c.field}" rows="2">${escapeHtml(val)}</textarea></td>`;
      }
      if (c.type === 'select') {
        return `<td><select class="${cls}" data-key="${key}" data-i="${i}" data-field="${c.field}">
          ${(c.options || []).map(o => `<option value="${escapeHtml(o)}" ${o === val ? 'selected' : ''}>${escapeHtml(o || '（未选）')}</option>`).join('')}
        </select></td>`;
      }
      if (c.type === 'tags') {
        const arr = Array.isArray(val) ? val : [];
        return `<td><input class="${cls}" data-key="${key}" data-i="${i}" data-field="${c.field}" data-tags="1"
          value="${escapeHtml(arr.join(' | '))}" placeholder="用 | 分隔"></td>`;
      }
      return `<td><input class="${cls}" data-key="${key}" data-i="${i}" data-field="${c.field}" value="${escapeHtml(val)}"></td>`;
    }).join('');

    return `<tr data-row="${i}">
      <td class="muted">${i + 1}</td>
      ${cells}
      <td>
        <button class="tag-btn ${row.needsReview ? 'warn' : 'ok'}" data-toggle="${i}" type="button"
          title="点击切换确认状态">${row.needsReview ? '待确认' : '已确认'}</button>
      </td>
      <td><button class="icon-btn" data-del="${i}" type="button" title="删除这一行">🗑</button></td>
    </tr>`;
  }).join('');

  return `<div class="table-wrap"><table class="tbl preview-tbl">
    <thead>${head}</thead>
    <tbody>${body}</tbody>
  </table></div>`;
}

/** 绑定表格编辑事件 */
export function bindEditableTable(container, { key, rows, onChange, onDelete, onToggle }) {
  container.querySelectorAll(`[data-key="${key}"]`).forEach(input => {
    input.addEventListener('change', () => {
      const i = Number(input.dataset.i);
      const field = input.dataset.field;
      let value = input.value;
      if (input.dataset.tags) value = value.split('|').map(s => s.trim()).filter(Boolean);
      onChange(i, field, value);
      input.classList.remove('need');
    });
  });
  container.querySelectorAll('[data-del]').forEach(btn => {
    btn.addEventListener('click', () => onDelete(Number(btn.dataset.del)));
  });
  container.querySelectorAll('[data-toggle]').forEach(btn => {
    btn.addEventListener('click', () => onToggle(Number(btn.dataset.toggle)));
  });
}

/** 分段 + 统计条 */
export function summaryBar(draft) {
  const s = draft.stats || {};
  const items = [
    { k: 'words', label: '单词', n: draft.words?.length || 0 },
    { k: 'sentences', label: '句子', n: draft.sentences?.length || 0 },
    { k: 'grammar', label: '语法', n: draft.grammar?.length || 0 },
    { k: 'questions', label: '题目', n: draft.questions?.length || 0 },
  ];
  return `<div class="preview-stats">
    ${items.map(i => `<div class="pv-stat"><b>${i.n}</b><span>${i.label}</span></div>`).join('')}
    <div class="pv-stat warn"><b>${s.needsReview || 0}</b><span>待确认</span></div>
  </div>`;
}
